import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { SupportedLanguage } from "../i18n";

/**
 * User State Interface
 *
 * Stores user-specific information including authentication state,
 * selected dealer, and language preferences.
 */
export interface UserState {
  userName: string;
  fullName: string;
  email: string;
  selectedDealerCode: string;
  language: SupportedLanguage;
  isAuthenticated: boolean;
}

const initialState: UserState = {
  userName: "",
  fullName: "",
  email: "",
  selectedDealerCode: "",
  language: "en-US",
  isAuthenticated: false,
};

/**
 * User Slice
 *
 * Manages global user state including:
 * - User identity (userName, email)
 * - Selected dealer code
 * - Language preference
 * - Authentication status
 */
export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    /**
     * Set user information on sign-in
     */
    setUser: (
      state,
      action: PayloadAction<{
        userName: string;
        fullName?: string;
        email?: string;
      }>
    ) => {
      state.userName = action.payload.userName;
      state.fullName = action.payload.fullName || "";
      state.email = action.payload.email || "";
      state.isAuthenticated = true;
    },

    /**
     * Set the selected dealer code
     */
    setSelectedDealer: (state, action: PayloadAction<string>) => {
      state.selectedDealerCode = action.payload;
    },

    /**
     * Set the user's language preference
     */
    setLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
      state.language = action.payload;
    },

    /**
     * Clear all user state on logout
     */
    logout: (state) => {
      state.userName = initialState.userName;
      state.fullName = initialState.fullName;
      state.email = initialState.email;
      state.selectedDealerCode = initialState.selectedDealerCode;
      state.language = initialState.language;
      state.isAuthenticated = initialState.isAuthenticated;
    },
  },
});

export const { setUser, setSelectedDealer, setLanguage, logout } =
  userSlice.actions;

export default userSlice.reducer;
